import { inject } from '@angular/core';
import { UnsavedChangesTrackerService } from '../../../app/services/unsaved-changes-tracker.service';
import { SIDEBAR_AREA } from '../../constants/sidebar-area';

export class SidebarConfiguration {
  public summaryAreaEnabled = true;
  public auditLogAreaEnabled = true;
  public editAreaEnabled = true;
  public linkAreaEnabled = false;
  public translationsAreaEnabled = true;
  public detailedTranslationsAreaEnabled = false;
  public translationsSettingsAreaEnabled = false;
  public reviewAreaEnabled = false;
  public mergeReviewAreaEnabled = false;

  public sideBarAreas = new Map<string, boolean>([
    [SIDEBAR_AREA.summary, true],
    [SIDEBAR_AREA.auditLog, false],
    [SIDEBAR_AREA.edit, false],
    [SIDEBAR_AREA.link, false],
    [SIDEBAR_AREA.translations, false],
    [SIDEBAR_AREA.detailedTranslations, false],
    [SIDEBAR_AREA.languageConfiguration, false],
    [SIDEBAR_AREA.review, false],
    [SIDEBAR_AREA.mergeReview, false],
  ]);

  public get innerSidebarTitle(): string {
    return this._innerSidebarTitle;
  }

  private _innerSidebarTitle = 'Summary'; // default, otherwise title is missing before first click on sidebar selector
  private readonly unsavedChangesService = inject(UnsavedChangesTrackerService);

  /**
   * Sets the initial area to be opened in the sidebar. Default is 'summary'.
   * @param area The area to be opened.
   */
  public set initArea(area: string) {
    this.openArea(area);
  }

  public openAuditLogs(): void {
    this._innerSidebarTitle = 'Audit Logs';
    this.openArea(SIDEBAR_AREA.auditLog);
  }

  public openEdit(): void {
    this._innerSidebarTitle = 'Edit';
    this.openArea(SIDEBAR_AREA.edit);
  }

  public openLink(): void {
    this._innerSidebarTitle = 'Linking';
    this.openArea(SIDEBAR_AREA.link);
  }

  public openDetails(): void {
    this._innerSidebarTitle = 'Summary';
    this.openArea(SIDEBAR_AREA.summary);
  }

  public openTranslations(): void {
    this._innerSidebarTitle = 'Translations';
    this.openArea(SIDEBAR_AREA.translations);
  }

  public openDetailedTranslations(): void {
    this._innerSidebarTitle = 'Translations';
    this.openArea(SIDEBAR_AREA.detailedTranslations);
  }

  public openLanguageConfiguration(): void {
    this._innerSidebarTitle = 'Language Configuration';
    this.openArea(SIDEBAR_AREA.languageConfiguration);
  }

  public openDtcReview(): void {
    this._innerSidebarTitle = 'Review';
    this.openArea(SIDEBAR_AREA.review);
  }

  public openDtcReviewMerge(): void {
    this._innerSidebarTitle = 'Merge Review';
    this.openArea(SIDEBAR_AREA.mergeReview);
  }

  public isAreaEnabled(area: string): boolean {
    return this.sideBarAreas.get(area) ?? false;
  }

  private openArea(area: string): void {
    if (
      this.unsavedChangesService.areThereAnyUnsavedChanges(() => {
        this.setActiveSidebarArea(area);
      })
    ) {
      return;
    }
    this.setActiveSidebarArea(area);
  }

  private setActiveSidebarArea(area: string): void {
    this.sideBarAreas.forEach((_, key) => {
      this.sideBarAreas.set(key, false);
      this.sideBarAreas.set(key, key === area);
    });
    this.setSidebarTitle();
  }

  private setSidebarTitle(): void {
    for (const [key, value] of this.sideBarAreas.entries()) {
      if (value) {
        this._innerSidebarTitle = key;
      }
    }
  }
}
